/**
 * Compose multiple regexes into a single regex.
 *
 * It takes the flags of all of the regexes, if they are the same.
 * In case of different flags, it throws an error.
 *
 * @param regexes - The regexes to compose.
 * @returns The composed regex.
 * @throws If the regexes have different flags.
 *
 * @example
 * const regex1 = /foo/i
 * const regex2 = /bar/i
 * const combined = composeRegex([regex1, regex2])
 * console.log(combined.test('foo')) // true
 * console.log(combined.test('bar')) // true
 * console.log(combined.test('baz')) // false
 */
function composeRegex(regexes: RegExp[]) {
    if (!Array.isArray(regexes)) {
        throw new Error('regexes must be an array')
    }

    if (regexes.length === 0) {
        throw new Error('At least one regex is required')
    }

    if (regexes.some((regex) => !(regex instanceof RegExp))) {
        throw new Error('All regexes must be instances of RegExp')
    }

    const combinedFlags: string[] = regexes.map((regex) => regex.flags)
    const [flags, ...otherFlags] = Array.from(new Set(combinedFlags))

    if (otherFlags.length > 0) {
        throw new Error(
            `All regexes must have the same flags. Got: ${flags} and ${otherFlags.join(', ')}`,
        )
    }

    return new RegExp(regexes.map((regex) => regex.source).join('|'), flags)
}

export default composeRegex
